import { useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useEffect } from "react"

import { GoodsReceiptDetailsLayout, GoodsReceiptFileFormDialog } from "../../../components"
import { GoodsReceiptProvider } from "../../../contexts"
import { useGetGoodsReceipt, useGoodsReceiptDetailsTabs, useGoodsReceiptStatusRedirect } from "../../../hooks"
import { GoodReceiptActionType } from "../../../types"
import { useStoredGoodsReceiptActions } from "../hooks"

import { GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"

export const StoredGoodsReceiptDetailsLayout: FC = () => {
  const { isTabletView, getBasicPathFor } = useAppView()
  const { goodsReceiptId } = useParams({
    from: "/app/warehouse-receiving/stored/$goodsReceiptId/_layout",
  })

  const { goodsReceipt, refetch } = useGetGoodsReceipt(goodsReceiptId)
  useGoodsReceiptStatusRedirect({ goodsReceipt, expectedStatus: GoodsReceiptStatus.Stored })
  const { tabs, activeTab } = useGoodsReceiptDetailsTabs({
    goodsReceiptId,
    baseRoute: "stored",
  })

  useEffect(() => {
    if (isTabletView) {
      window.scrollTo(0, 0)
    }
  }, [isTabletView])

  const { actionState, handleDialogClose, handleEditGoodsReceipt, handleUpload } = useStoredGoodsReceiptActions({
    goodsReceipt,
    onRefetch: refetch,
  })

  if (!goodsReceipt) {
    return null
  }

  return (
    <GoodsReceiptProvider
      value={{
        goodsReceipt,
        onEdit: handleEditGoodsReceipt,
        onUpload: handleUpload,
      }}
    >
      <GoodsReceiptDetailsLayout
        goodsReceipt={goodsReceipt}
        tabs={tabs}
        activeTab={activeTab}
        backToPath={`${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/stored`}
        dataTestId='stored-goods-receipt-details'
        palletsCount={goodsReceipt.softReceiveData?.deliveredPalletsCount ?? 0}
      />

      {actionState?.type === GoodReceiptActionType.FileUpload && (
        <GoodsReceiptFileFormDialog onClose={handleDialogClose} onRefresh={refetch} goodsReceiptId={goodsReceiptId} />
      )}
    </GoodsReceiptProvider>
  )
}
